import {
  computeLineDisplayAmount,
  isLineSuppressedForPdf,
} from "./formatInvoicePdfLine";
import { buildInvoicePdfLineTree } from "./buildInvoicePdfLineTree";
import { resolveInvoicePdfWatermark } from "./resolveInvoicePdfWatermark";
import { sortInvoiceLinesByProfileServiceOrder } from "../sortInvProfileServices";

const HIBBERT_REMITTANCE_LINES = [
  "Please attach a copy of",
  "the invoice with your",
  "check and remit to:",
  "The Hibbert Group",
  "Lbx 41765",
  "PO Box 8500",
  "Philadelphia, PA 19178",
];

function trimText(value) {
  return String(value ?? "").trim();
}

function formatServiceMonthLabel(yyyyMm) {
  const raw = trimText(yyyyMm);
  const match = /^(\d{4})-(\d{2})$/.exec(raw);
  if (!match) {
    return raw || "—";
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) {
    return raw;
  }
  return new Date(year, month - 1, 1).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
}

function formatServiceMonthAbbrev(yyyyMm) {
  const raw = trimText(yyyyMm);
  const match = /^(\d{4})-(\d{2})$/.exec(raw);
  if (!match) {
    return "";
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) {
    return "";
  }
  return new Date(year, month - 1, 1)
    .toLocaleDateString("en-US", { month: "short" })
    .replace(".", "")
    .toUpperCase();
}

function formatInvoiceDateForPdf(value) {
  if (value) {
    const parsed = value instanceof Date ? value : new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
      });
    }
  }
  return new Date().toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
}

function formatClientAddress(customer) {
  if (!customer) {
    return [];
  }
  const lines = [
    trimText(customer.addressLine1),
    trimText(customer.addressLine2),
    [trimText(customer.city), trimText(customer.state), trimText(customer.zip)]
      .filter(Boolean)
      .join(", ")
      .replace(/,\s*,/g, ","),
  ].filter(Boolean);
  return lines;
}

function resolveNetTermsLabel(contracts) {
  const rows = Array.isArray(contracts) ? contracts : [];
  const active = rows.find((row) => String(row.status ?? "").trim().toLowerCase() === "active");
  const candidate = active ?? rows[0];
  const netTerms = candidate?.netTerms;
  if (netTerms == null || netTerms === "") {
    return "—";
  }
  return `Net ${netTerms} Days`;
}

function resolveContactName(customer, accountExecutives) {
  const contact = trimText(customer?.contactName);
  if (contact) {
    return contact;
  }
  const execs = Array.isArray(accountExecutives) ? accountExecutives : [];
  const firstExec = execs.find((row) => trimText(row.displayName));
  return trimText(firstExec?.displayName) || "—";
}

function resolveJobNumberLabel(jobNos, serviceMonth) {
  const firstJob = (Array.isArray(jobNos) ? jobNos : []).map((job) => trimText(job)).find(Boolean);
  if (!firstJob) {
    return "—";
  }
  const monthAbbrev = formatServiceMonthAbbrev(serviceMonth);
  return monthAbbrev ? `${firstJob} ${monthAbbrev}` : firstJob;
}

function isPostageChildLine(line) {
  const invoicePostageId = Number(line?.invoicePostageId ?? line?.invoice_postage_id);
  return Number.isFinite(invoicePostageId) && invoicePostageId > 0;
}

/**
 * Postage added to POST_STATEMENT is stored as separate detail rows with
 * {@code invoice_postage_id}. For PDF output, roll those amounts into the parent
 * profile line and omit the individual postage entries.
 */
function rollupPostageLinesForPdf(mappedLines) {
  const postageTotalByDetailId = new Map();

  for (const line of mappedLines ?? []) {
    if (!isPostageChildLine(line)) {
      continue;
    }
    const detailId = Number(line.invProfileDetailId);
    if (!Number.isFinite(detailId) || detailId <= 0) {
      continue;
    }
    const amount = computeLineDisplayAmount(line, line.profileDetail);
    postageTotalByDetailId.set(detailId, (postageTotalByDetailId.get(detailId) ?? 0) + amount);
  }

  return (mappedLines ?? [])
    .filter((line) => !isPostageChildLine(line))
    .map((line) => {
      const detailId = Number(line.invProfileDetailId);
      const rollup =
        Number.isFinite(detailId) && detailId > 0
          ? postageTotalByDetailId.get(detailId) ?? 0
          : 0;
      if (!rollup) {
        return line;
      }
      return {
        ...line,
        pdfRollupAmount: rollup,
      };
    });
}

function mapSavedLine(line, index, profileDetails) {
  const invProfileDetailId = Number(line.invProfileDetailId ?? line.inv_profile_detail_id);
  const invoicePostageId = Number(line.invoicePostageId ?? line.invoice_postage_id);
  const profileDetail =
    Number.isFinite(invProfileDetailId) && invProfileDetailId > 0
      ? (profileDetails ?? []).find(
          (row) => Number(row.invProfileDetailId) === invProfileDetailId
        ) ?? null
      : null;
  const rateValue = line.rate != null ? Number(line.rate) : Number.NaN;
  const amountValue = line.amount != null ? Number(line.amount) : Number.NaN;
  const quantityValue = line.quantity != null ? Number(line.quantity) : Number.NaN;
  return {
    id: line.invoiceLineId ?? index + 1,
    description: trimText(line.defaultInvoiceDescription ?? line.description),
    quantity: Number.isFinite(quantityValue) ? quantityValue : null,
    rate: Number.isFinite(rateValue) ? rateValue : null,
    amount: Number.isFinite(amountValue) ? amountValue : 0,
    overrideFlag: Boolean(line.overrideFlag),
    overrideAmount:
      line.overrideAmount != null && Number.isFinite(Number(line.overrideAmount))
        ? Number(line.overrideAmount)
        : null,
    overrideQuantity:
      line.overrideQuantity != null && Number.isFinite(Number(line.overrideQuantity))
        ? Number(line.overrideQuantity)
        : null,
    suppressed: Boolean(line.suppressed),
    invProfileDetailId:
      Number.isFinite(invProfileDetailId) && invProfileDetailId > 0 ? invProfileDetailId : null,
    invoicePostageId:
      Number.isFinite(invoicePostageId) && invoicePostageId > 0 ? invoicePostageId : null,
    profileDetail,
  };
}

export function buildInvoicePdfModel({
  draft,
  customer,
  contracts,
  accountExecutives,
  profileDetails,
  jobNos,
  clientName,
}) {
  const rawLines = Array.isArray(draft?.lines) ? draft.lines : [];
  const sortedLines = sortInvoiceLinesByProfileServiceOrder(
    rawLines,
    profileDetails,
    draft?.invProfileId
  );
  const mappedLines = rollupPostageLinesForPdf(
    sortedLines
      .map((line, index) => mapSavedLine(line, index, profileDetails))
      .filter((line) => !isLineSuppressedForPdf(line))
  );

  const lineTree = buildInvoicePdfLineTree(mappedLines);

  const subtotal = mappedLines.reduce(
    (sum, line) => sum + computeLineDisplayAmount(line, line.profileDetail),
    0
  );
  const headerAdjustments = Number(draft?.adjustments);
  const total =
    subtotal + (Number.isFinite(headerAdjustments) ? headerAdjustments : 0);

  const resolvedClientName =
    trimText(clientName) ||
    trimText(customer?.custName) ||
    trimText(draft?.custgroup) ||
    "—";

  return {
    invoiceNumber: trimText(draft?.invoiceNumber) || String(draft?.invoiceId ?? "—"),
    totalDue: total,
    salesCode: "TBD",
    purchaseOrderNumber: trimText(draft?.poNumber) || "—",
    terms: resolveNetTermsLabel(contracts),
    invoiceDate: formatInvoiceDateForPdf(null),
    jobNumber: resolveJobNumberLabel(jobNos, draft?.serviceMonth),
    invoiceProgramName: trimText(draft?.invoiceProgramName) || "—",
    serviceMonthLabel: formatServiceMonthLabel(draft?.serviceMonth),
    contactName: resolveContactName(customer, accountExecutives),
    clientName: resolvedClientName,
    clientAddressLines: formatClientAddress(customer),
    lineTree,
    invoiceDistribution: "TBD",
    remittanceLines: HIBBERT_REMITTANCE_LINES,
    watermark: resolveInvoicePdfWatermark(draft?.status),
  };
}
